import type { UnifiedModel } from '../types/unified-model.types'
import { cn } from '@/lib/utils'

type Health = 'healthy' | 'degraded' | 'down' | 'unknown'

const BAR_HEIGHTS = ['h-1.5', 'h-2.5', 'h-3.5']

const HEALTH_META: Record<
  Health,
  { label: string; litBars: number; bar: string }
> = {
  healthy: {
    label: 'Operational',
    litBars: 3,
    bar: 'bg-[var(--color-success)]',
  },
  degraded: {
    label: 'Degraded',
    litBars: 2,
    bar: 'bg-[var(--color-warning)]',
  },
  down: {
    label: 'Disrupted',
    litBars: 1,
    bar: 'bg-[var(--color-error)]',
  },
  unknown: {
    label: 'No traffic',
    litBars: 0,
    bar: '',
  },
}

function toHealth(rate: number | null): Health {
  if (rate === null) return 'unknown'
  if (rate >= 99) return 'healthy'
  if (rate >= 95) return 'degraded'
  return 'down'
}

/**
 * Signal-style health indicator for the last 24h. Rate counts only 5xx as
 * failures; a window with no eligible traffic is "unknown", not 100%.
 */
export function ModelUptimeStrip({ model }: { model: UnifiedModel }) {
  const segments = model.uptimeHistory ?? []
  const successes = segments.reduce((n, s) => n + s.successCount, 0)
  const failures = segments.reduce((n, s) => n + s.serverErrorCount, 0)
  const eligible = successes + failures
  const rate = eligible === 0 ? null : (successes / eligible) * 100
  const health = toHealth(rate)
  const meta = HEALTH_META[health]
  const rateText = rate === null ? '—' : `${rate.toFixed(2)}%`

  return (
    <div
      className="flex items-center gap-2 text-caption text-[var(--color-text-tertiary)]"
      title={
        rate === null
          ? 'No requests in the last 24 hours'
          : `${meta.label} · ${rateText} success over the last 24h · ${failures} server error${failures === 1 ? '' : 's'}`
      }
    >
      <span>Uptime status</span>
      <span
        className="flex h-3.5 items-end gap-0.5"
        role="img"
        aria-label={`Status: ${meta.label}, ${rateText} uptime over 24 hours`}
      >
        {BAR_HEIGHTS.map((height, i) => (
          <span
            key={height}
            className={cn(
              'w-[3px] rounded-full transition-colors duration-300 ease-brisk',
              height,
              i < meta.litBars ? meta.bar : 'bg-[var(--color-border-strong)]',
            )}
          />
        ))}
      </span>
    </div>
  )
}
